import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, isArray, noop } from "@regrello/core-utils";
import type {
  FieldFields,
  FieldInstanceFields,
  FormFieldFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import type {
  CustomFieldFrontendSelectableOption,
  SelectFieldPluginSingleValuedFrontendValue,
} from "../../../customFields/plugins/types/CustomFieldFrontendSelectableOption";
import { INACTIVE_SELECTABLE_OPTION_ID } from "../../../customFields/plugins/types/CustomFieldFrontendSelectableOption";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import { extractAllowedValuesFromFieldVersionForFrontend } from "../../../customFields/plugins/utils/extractAllowedValuesForFrontend";
import {
  normalizeSelectValue,
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloFormFieldSelect } from "../../../formFields/RegrelloFormFieldSelect";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { validateByConstraints } from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellSelect } from "./RegrelloEditableCellSelect";

type VALUE = SelectFieldPluginSingleValuedFrontendValue | CustomFieldFrontendSelectableOption[];

export class SpectrumSelectFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.select";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.SELECT;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "STRING");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.SELECT,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(_valueConstraints: SpectrumValueConstraintFields[]) {
    return EMPTY_ARRAY;
  }

  public getConstraintConditionOperators() {
    return {
      EQ: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      IS_ZERO: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_NOT_ZERO: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      NOT: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_IS_ONE_OF: {
        label: t`Is any of`,
        inputCount: 1,
        isMultiselect: true,
      },
      STRING_IS_NONE_OF: {
        label: t`Is none of`,
        inputCount: 1,
        isMultiselect: true,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    return this.renderFormField(props.fieldInstance?.spectrumFieldVersion.field ?? field, props);
  };

  public renderPreviewFormField() {
    return (
      <RegrelloFormFieldSelect
        disabled={true}
        getOptionLabel={(option) => option}
        onChange={noop}
        options={[]}
        size="medium"
        value=""
      />
    );
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public override parseTableValue(fieldVersion: SpectrumFieldVersionFields, valueIn: unknown): VALUE {
    if (valueIn == null) {
      return null;
    }

    if (
      typeof valueIn === "object" &&
      "id" in valueIn &&
      "value" in valueIn &&
      "displayValue" in valueIn &&
      typeof valueIn.id === "number" &&
      typeof valueIn.value === "string" &&
      typeof valueIn.displayValue === "string"
    ) {
      return valueIn as VALUE;
    }

    if (typeof valueIn === "string") {
      const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(fieldVersion);
      const matchingValue = allowedValues.find((allowedValue) => allowedValue.displayValue === valueIn);
      return matchingValue ?? null;
    }

    return null;
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Pick<ColDef<TData, VALUE>, "onCellValueChanged" | "cellClassRules">,
    options: {
      fieldInstance: FieldInstanceFields;
      formField: FormFieldFields | undefined;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    const spectrumFieldVersion = options.fieldInstance.spectrumFieldVersion;
    if (spectrumFieldVersion == null) {
      throw new Error("SpectrumFieldVersion is null on fieldInstance, this should never happen");
    }

    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-center-aligned-cell": (_) => true,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellEditor: RegrelloEditableCellSelect,
      cellEditorParams: {
        fieldInstance: options.fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      valueParser: (params) => {
        return this.parseTableValue(spectrumFieldVersion, params.newValue);
      },
      valueFormatter: (params: ValueFormatterParams<TData, VALUE>): string => {
        const value = this.extractValueFromParamsValue(params.value ?? null);
        if (value != null && this.isNonEmptyCellValue(value)) {
          return value.displayValue;
        }

        return "";
      },
      cellRenderer: (params: ValueFormatterParams<TData, VALUE>): ReactNode => {
        const valueString = options.placeholderRenderer != null ? options.placeholderRenderer(params) : "";

        const value = this.extractValueFromParamsValue(params.value ?? null);
        if (value != null && this.isNonEmptyCellValue(value)) {
          return value.displayValue;
        }

        return valueString;
      },
    };
  }

  /**
   * Extracts a single-valued value from a potentially multi-valued value input.
   */
  private extractValueFromParamsValue(value: VALUE | null): SelectFieldPluginSingleValuedFrontendValue {
    if (value == null) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }

    return value;
  }

  /**
   * Returns true if the value is a supertype of a valid { value: string, id: number } object.
   */
  private isNonEmptyCellValue(value: SelectFieldPluginSingleValuedFrontendValue): boolean {
    return (
      value != null &&
      typeof value === "object" &&
      "value" in value &&
      "id" in value &&
      typeof value.value === "string" &&
      typeof value.id === "number" &&
      value.value !== "" &&
      value.id !== INACTIVE_SELECTABLE_OPTION_ID
    );
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: SelectFieldPluginSingleValuedFrontendValue | CustomFieldFrontendSelectableOption[],
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      stringValue: Array.isArray(frontendValue) ? frontendValue[0].value : (frontendValue?.value ?? ""),
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const valueAsString = value.trim();

    const result = validateRequired(valueAsString, options.isRequired);
    if (result != null) {
      return result;
    }

    const caseNormalizedString = normalizeSelectValue(valueAsString);
    const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(options.fieldVersion);
    const selectedInternalValue = allowedValues.find(
      (option) => normalizeSelectValue(option.value) === caseNormalizedString,
    );

    if (selectedInternalValue == null) {
      return {
        value: valueAsString,
        info: [
          {
            message: t`Must be a recognized value`,
            level: "error",
          },
        ],
      };
    }

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, selectedInternalValue);
    if (!spectrumValidationResult.isValid) {
      return {
        value: valueAsString,
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return {
      value: selectedInternalValue.value,
    };
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    // (max): Single select can either be an array of options, or a single option.
    if (value == null || (isArray(value) && value.length === 0)) {
      if (isRequired) {
        return { isValid: false, errors: [t`This field is required`] };
      }
      return { isValid: true, errors: EMPTY_ARRAY };
    }

    if (!this.isSelectableOptionType(value)) {
      return { isValid: false, errors: [t`Must contain only selectable options`] };
    }

    return this.validateValueInternal(fieldVersion, value);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    const allowedValueOptions = extractAllowedValuesFromFieldVersionForFrontend(fieldVersion);

    if (this.isSingleSelectableOptionType(value)) {
      if (value != null) {
        const hasValidAllowedValue =
          allowedValueOptions.find((option) => option.value === value.value && option.id === value.id) != null;
        if (!hasValidAllowedValue) {
          return { isValid: false, errors: [t`Must be a recognized value`] };
        }
      }
    } else {
      const hasValidAllowedValues = value.every(
        (selectedValue) =>
          allowedValueOptions.find(
            (option) => option.value === selectedValue.value && option.id === selectedValue.id,
          ) != null,
      );
      if (!hasValidAllowedValues) {
        return { isValid: false, errors: [t`Must be a recognized value`] };
      }
    }

    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }

  private isSingleSelectableOptionType(value: unknown): value is SelectFieldPluginSingleValuedFrontendValue {
    return (
      value === null ||
      (value != null &&
        typeof value === "object" &&
        "id" in value &&
        typeof value.id === "number" &&
        "value" in value &&
        typeof value.value === "string" &&
        "displayValue" in value &&
        typeof value.displayValue === "string")
    );
  }

  private isSelectableOptionType(value: unknown): value is VALUE {
    if (isArray(value)) {
      return value.every((entry) => entry != null && this.isSingleSelectableOptionType(entry));
    }
    return this.isSingleSelectableOptionType(value);
  }
}
